// The task list as `tasks_queue.md` — a file saved into a repo and handed to Claude Code to work
// through, top to bottom.
//
// It carries two things and nothing else: an instruction block saying how to work the queue, and
// the task prompts one after another. It was a spreadsheet before, on the assumption that a
// Python script would read it and drive Claude from it; that middle step is gone, and a file
// meant to be READ needs prose, not columns.
//
// The DATA comes from `/orbit-dev/tasks/export`, which is a separate route from the list because
// it signs a read URL per attachment — a cost only an export should pay. Those URLs are good for
// days rather than the half-hour a page needs, which is what makes a linked screenshot in a
// queue worth linking at all.

import { taskAsPrompt } from './format.js';
import { triggerDownload } from '../../shared/http/download.js';

// The name is fixed, not stamped with a date: the file is saved into a folder and referred to by
// name, and a queue called `tasks_queue-2026-08-18.md` is one the instructions cannot name.
export const QUEUE_FILE_NAME = 'tasks_queue.md';

/**
 * How the queue is meant to be worked.
 *
 * Written to a reader who will do exactly what it says, so it says what to do at the two moments
 * that actually decide whether this works: when a task is clear (keep going without checking in)
 * and when it is not (stop, ask one question, wait). Without the first, the run stops after every
 * task to ask permission; without the second, an under-specified task gets guessed at and the
 * guess is discovered later.
 *
 * The last paragraph exists because "ask if unsure" is otherwise read far too broadly — most
 * decisions in a task are ordinary judgment, and a queue that pauses on each of them is worse
 * than no queue.
 */
const INSTRUCTIONS = `## How to work this queue

Work through the tasks below in order, from the top. For each one:

1. Read the whole task first, including any screenshot it links to.
2. If the task tells you everything you need, do the work. Say briefly what you changed,
   then move straight on to the next task — do not stop to ask whether to continue.
3. If it does not, stop. Ask the single question that unblocks you and wait for an answer.
   Do not guess at the intent, and do not skip ahead to a later task while you wait.
4. When the answer arrives, finish that task and carry on down the list.

Ambiguity means you cannot tell what a correct result would look like, or the task could
reasonably be built two different ways and the wrong one would be expensive to undo. A
decision you can make and describe in a sentence is not ambiguity — make it, say what you
chose, and keep going.

Do not reorder the queue, and do not start a task twice.`;

/** ISO 8601. The reader is a program, so no locale formatting. */
function iso(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString();
}

/**
 * Tasks in the order they are shown on screen.
 *
 * `orderedIds` comes from the page, which is the only thing that knows its own grouping —
 * rejected first then by module in My Work, by environment in the QA Queue, by section in the
 * Pipeline. Re-deriving any of that here would be a second copy of the display rules that could
 * disagree with the first.
 *
 * Anything the page did not list goes last rather than being dropped: a task filed between the
 * page loading and the button being pressed is still a task, and silently missing from a work
 * queue is the worst way for it to be handled.
 */
function inDisplayOrder(tasks, orderedIds) {
  if (!orderedIds?.length) return tasks;
  const rank = new Map(orderedIds.map((id, i) => [String(id), i]));
  return [...tasks].sort((a, b) => {
    const ai = rank.has(String(a.id)) ? rank.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
    const bi = rank.has(String(b.id)) ? rank.get(String(b.id)) : Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
}

/**
 * The file as text.
 *
 * Separate from the download so it can be generated and asserted outside a browser — this file
 * is an interface to a working session, and "the build passed" says nothing about whether the
 * queue reads correctly.
 */
export function buildQueue({ payload, viewLabel, orderedIds, includeIds = null }) {
  const { options } = payload;
  // `includeIds` is the ticked rows. Null means the whole view — not "none", which is what an
  // empty array would mean and would produce a queue with no tasks in it.
  const wanted = includeIds ? new Set(includeIds.map(String)) : null;
  const chosen = wanted
    ? (payload.tasks || []).filter(t => wanted.has(String(t.id)))
    : (payload.tasks || []);
  const tasks = inDisplayOrder(chosen, orderedIds);
  const total = tasks.length;

  const header = [
    `# Task queue — ${viewLabel}`,
    '',
    `Generated ${iso(payload.generated_at)} from Orbit Development.`,
    `${total} task${total === 1 ? '' : 's'}${wanted ? ', selected by hand' : ''}.`,
  ];
  // Only mentioned when something actually links to a blob. A line about expiring links in a
  // queue with no attachments is a warning about nothing.
  if (tasks.some(t => (t.attachments || []).some(a => a.url))) {
    header.push(`Attachment links stop working after ${iso(payload.url_expires_at)}.`);
  }

  const body = tasks.map((task, i) => taskAsPrompt(
    task, options, task.attachments || [], { index: i + 1, total },
  ));

  return {
    text: [header.join('\n'), INSTRUCTIONS, ...body].join('\n\n---\n\n') + '\n',
    fileName: QUEUE_FILE_NAME,
    tasks: total,
    attachments: tasks.reduce((n, t) => n + (t.attachments || []).length, 0),
  };
}

/** Build it and hand it to the browser. */
export function exportQueue(args) {
  const { text, fileName, tasks, attachments } = buildQueue(args);
  triggerDownload(new Blob([text], { type: 'text/markdown;charset=utf-8' }), fileName);
  return { tasks, attachments };
}
